import { Button, Flex, FormControl, FormErrorMessage, FormLabel, Text } from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { useQueryClient } from '@tanstack/react-query';

import { CognitoUser, useToast, ArrowLeftOutline } from '@aslanpay/portal-common-lib';

import PhoneNumberInput from 'app/components/UI/Inputs/PhoneNumberInput';

import useSignIn from 'app/hooks/useSignIn';
import useSignUpIndividual from 'app/hooks/useSignUpIndividual';

import { useAuthenticationStore } from 'app/store';

import { authenticationSchema } from 'app/utils/schemas';

import { AuthenticationStepProps, EnableTwoFactorFormFields } from '../types';

function EnableTwoFactor({ onSetStep }: AuthenticationStepProps) {
  const queryClient = useQueryClient();

  const toast = useToast();

  const authenticationData = useAuthenticationStore((state) => state.authenticationData);
  const setAuthenticationData = useAuthenticationStore((state) => state.setAuthenticationData);
  const resetAuthenticationData = useAuthenticationStore((state) => state.resetAuthenticationData);

  const {
    control,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<EnableTwoFactorFormFields>({
    mode: 'onChange',
    resolver: yupResolver(authenticationSchema.enableTwoFactor),
    defaultValues: {
      phoneNumber: authenticationData.phoneNumber,
    },
  });

  const { mutate: signIn, isLoading: isSigningIn } = useSignIn({
    onSuccess: (data: CognitoUser) => {
      if (data.challengeName === 'SMS_MFA') {
        setAuthenticationData({ ...authenticationData, cognitoUser: data });
        onSetStep('confirmLogin');
      } else {
        queryClient.invalidateQueries({ queryKey: ['getAuthenticatedUser'] });
        resetAuthenticationData();
      }
    },
    onError: (error) => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: error.message,
      });
    },
  });

  const { mutate: signUpIndividual, isLoading: isSigningUp } = useSignUpIndividual({
    onSuccess: () => {
      signIn({ email: authenticationData.email, password: authenticationData.password });
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Please contact support',
      });
    },
  });

  const handleBack = () => {
    setAuthenticationData({ ...authenticationData, phoneNumber: getValues('phoneNumber') });
    onSetStep('userDetails');
  };

  const handleEnableTwoFactor = (data: EnableTwoFactorFormFields) => {
    setAuthenticationData({ ...authenticationData, phoneNumber: data.phoneNumber });

    signUpIndividual({
      phoneNumber: data.phoneNumber.split(' ').join(''),
      signUpToken: authenticationData.signUpToken,
      password: authenticationData.password,
      firstName: authenticationData.firstName,
      lastName: authenticationData.lastName,
    });
  };

  return (
    <Flex flexDir="column" mt="35px">
      <Button
        alignSelf="flex-start"
        size="sm"
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="20px" />}
        onClick={handleBack}
      >
        Back
      </Button>
      <Text as="h2" textStyle="h2" color="brand.darkPurple" mt="25px">
        Enable two-factor authentication
      </Text>
      <Text as="h5" textStyle="h5Medium" color="brand.mutedPurple" mt="15px" mb="35px">
        Please enter your mobile number below
      </Text>
      <form onSubmit={handleSubmit(handleEnableTwoFactor)} noValidate>
        <FormControl isInvalid={!!errors?.phoneNumber?.message} isRequired>
          <FormLabel>Mobile number</FormLabel>
          <Controller
            control={control}
            name="phoneNumber"
            render={({ field: { onChange, onBlur, value } }) => (
              <PhoneNumberInput
                defaultValue={value}
                placeholder="Mobile number"
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          <FormErrorMessage>{errors?.phoneNumber?.message}</FormErrorMessage>
        </FormControl>
        <Text mt="42px" mb="22px" color="system.error" textStyle="extraSmallMedium">
          * mandatory
        </Text>
        <Button
          w="full"
          size="lg"
          type="submit"
          isLoading={isSigningUp || isSigningIn}
          isDisabled={!isValid}
        >
          Next
        </Button>
      </form>
    </Flex>
  );
}

export default EnableTwoFactor;
